import React, { useState, useRef } from 'react'
import classes from './index.module.css'
import uploadIcon from '../../icons/uploadIcon.svg'
import editIcon from '../../icons/edit-icon.svg'
import PropTypes from 'prop-types'
import errorToast from '../../Toasts/ErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import Loader from '../../GeneralComponents/Loader'

const BackdropUploader = ({ collectionId, backdropImage, isPublic, setPendingBackdropFile, onUploadComplete, iconOnly }) => {
  const inputRef = useRef()
  const queryClient = useQueryClient()
  const { getCollectionHeaderImageUrl, uploadCollectionImage } = useAPI()
  const [isUploaderOpen, setIsUploaderOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState(null)

  const handleSelectBackdrop = async () => {
    if (!file || file.length === 0) return
    const selectedFile = file[0]

    // Backdrop images must be PNG
    if (selectedFile.type !== 'image/png') {
      errorToast('Backdrop images must be PNG format.')
      return
    }

    try {
      const image = new Image()
      image.src = URL.createObjectURL(selectedFile)
      await image.decode()
      const { width } = image
      if (width < 720) {
        errorToast('The image must be at least 720px wide.')
        return
      }

      // Deferred mode: store the file for later upload
      if (setPendingBackdropFile) {
        setPendingBackdropFile(selectedFile)
        setFile(null)
        setIsUploaderOpen(false)
        return
      }

      // Immediate mode: upload directly
      setIsLoading(true)
      const uploadLink = await getCollectionHeaderImageUrl(
        collectionId,
        selectedFile.type,
        selectedFile.size,
        'background'
      )
      await uploadCollectionImage(
        [uploadLink.upload_url[0]],
        selectedFile,
        selectedFile.type
      )
      if (onUploadComplete) {
        onUploadComplete(URL.createObjectURL(selectedFile))
      }
      queryClient.invalidateQueries({
        queryKey: ['collection', collectionId, isPublic],
      })
    } catch (e) {
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setFile(null)
      setIsLoading(false)
      setIsUploaderOpen(false)
    }
  }

  const handleDragOver = event => {
    event.preventDefault()
  }

  const handleDrop = event => {
    event.preventDefault()
    event.stopPropagation()
    setFile(event.dataTransfer.files)
  }

  const handleContainerClick = event => {
    event.stopPropagation()
  }

  function onOpen() {
    setIsUploaderOpen(true)
  }

  function onClose() {
    setIsUploaderOpen(false)
    setFile(null)
  }

  return (
    <>
      {iconOnly ? (
        <button
          className={classes.editIconButton}
          type="button"
          onClick={onOpen}
          title="Edit gradient image"
        >
          <img src={editIcon} alt="Edit" className={classes.editIcon} />
        </button>
      ) : (
        <button
          className={
            backdropImage ? classes.backdropButtonChange : classes.backdropButtonUpload
          }
          type="button"
          onClick={onOpen}
        >
          {backdropImage ? 'Change Backdrop' : 'Add Backdrop'}
        </button>
      )}

      {isUploaderOpen && !file && (
        <div className={classes.uploaderBgContainer} onClick={onClose}>
          <div
            className={classes.uploaderContainer}
            onClick={handleContainerClick}
          >
            <div className={classes.uploaderHeader}>
              <span className={classes.uploaderTitle}>Collection Gradient</span>
              <span className={classes.uploaderSubtitle}>
                Upload a transparent PNG for the collection gradient.
                The image must be at least 720px wide.
              </span>
            </div>
            <div
              className={classes.uploaderWrapper}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => inputRef.current.click()}
              style={{ cursor: 'pointer' }}
            >
              {backdropImage && (
                <>
                  <img className={classes.uploadedImage} src={backdropImage} />
                  <div className={classes.uploadedImageOverlay} />
                </>
              )}
              <input
                accept="image/png"
                type="file"
                onChange={event => setFile(event.target.files)}
                hidden
                ref={inputRef}
              />
              <div className={classes.uploaderImageContainer}>
                <img
                  className={classes.uploaderImage}
                  src={uploadIcon}
                  alt="upload_icon"
                />
              </div>
              <div className={classes.uploadTextSection}>
                <button
                  onClick={(e) => { e.stopPropagation(); inputRef.current.click() }}
                  className={classes.uploaderButton}
                >
                  Click to Upload
                </button>
                <span className={classes.uploaderText}> or Drag and Drop</span>
              </div>
            </div>
            <div className={classes.uploaderBtnGroup}>
              <button className={classes.uploaderCancel} onClick={onClose}>
                Cancel
              </button>
              <button
                className={classes.uploaderSave}
                disabled={!file}
                onClick={handleSelectBackdrop}
              >
                Upload Gradient
              </button>
            </div>
          </div>
        </div>
      )}

      {isUploaderOpen && file && (
        <div className={classes.uploaderBgContainer} onClick={onClose}>
          <div
            className={classes.uploaderContainer}
            onClick={handleContainerClick}
          >
            <div className={classes.uploaderHeader}>
              <span className={classes.uploaderTitle}>Collection Gradient</span>
              <span className={classes.uploaderSubtitle}>
                Upload a transparent PNG for the collection gradient.
                The image must be at least 720px wide.
              </span>
            </div>
            <div
              className={classes.uploaderWrapper}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => inputRef.current.click()}
              style={{ cursor: 'pointer' }}
            >
              {file && file.length > 0 && (
                <>
                  <img
                    className={classes.uploadedImage}
                    src={URL.createObjectURL(file[0])}
                  />
                  <div className={classes.uploadedImageOverlay} />
                </>
              )}
              <input
                accept="image/png"
                type="file"
                onChange={event => setFile(event.target.files)}
                hidden
                ref={inputRef}
              />
              <div className={classes.uploaderImageContainer}>
                <img
                  className={classes.uploaderImage}
                  src={uploadIcon}
                  alt="upload_icon"
                />
              </div>
              <div className={classes.uploadTextSection}>
                <button
                  onClick={(e) => { e.stopPropagation(); inputRef.current.click() }}
                  className={classes.uploaderButton}
                >
                  Click to Upload
                </button>
                <span className={classes.uploaderText}> or Drag and Drop</span>
              </div>
            </div>
            {!isLoading && (
              <div className={classes.uploaderBtnGroup}>
                <button className={classes.uploaderCancel} onClick={onClose}>
                  Cancel
                </button>
                <button
                  className={classes.uploaderSave}
                  onClick={handleSelectBackdrop}
                >
                  Upload Gradient
                </button>
              </div>
            )}
            <Loader isLoading={isLoading} />
          </div>
        </div>
      )}
    </>
  )
}

BackdropUploader.propTypes = {
  collectionId: PropTypes.string,
  backdropImage: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  isPublic: PropTypes.bool,
  setPendingBackdropFile: PropTypes.func,
  onUploadComplete: PropTypes.func,
  iconOnly: PropTypes.bool,
}

export default BackdropUploader
